/**
 * AdRow component for displaying individual ads
 * Shows thumbnail, name, media type indicator, and selection checkbox
 * Supports both video and image ads as importable
 */

import { Video, Image, Clock } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { MetaAd } from '@/types/meta';

interface AdRowProps {
  ad: MetaAd;
  selected: boolean;
  onSelect: (adId: string, selected: boolean) => void;
}

/** Check if an ad is importable (video or image) */
function isImportable(ad: MetaAd): boolean {
  return ad.isVideo || ad.mediaType === 'image';
}

export function AdRow({ ad, selected, onSelect }: AdRowProps) {
  const importable = isImportable(ad);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelect(ad.id, e.target.checked);
  };

  // Format duration as MM:SS
  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 border-b border-border-subtle last:border-b-0',
        'ml-12', // Indent under ad set
        !importable && 'opacity-50'
      )}
    >
      {/* Checkbox - enabled for video and image ads */}
      <input
        type="checkbox"
        checked={selected}
        onChange={handleCheckboxChange}
        disabled={!importable}
        className={cn(
          'h-4 w-4 rounded border-border-subtle text-brand focus:ring-brand/20',
          !importable && 'cursor-not-allowed'
        )}
        aria-label={importable ? `Select ${ad.name}` : 'Unsupported format'}
      />

      {/* Thumbnail or placeholder */}
      <div className="flex-shrink-0 w-16 h-10 rounded overflow-hidden bg-surface-subtle">
        {ad.thumbnailUrl ? (
          <img
            src={ad.thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              // Hide broken image by setting opacity to 0
              (e.target as HTMLImageElement).style.opacity = '0';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {ad.mediaType === 'image' ? (
              <Image className="h-5 w-5 text-muted" />
            ) : ad.isVideo ? (
              <Video className="h-5 w-5 text-muted" />
            ) : (
              <Image className="h-5 w-5 text-muted" />
            )}
          </div>
        )}
      </div>

      {/* Ad info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-primary truncate">
            {ad.name}
          </span>
          {/* Media type indicator */}
          {ad.mediaType === 'image' ? (
            <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">
              <Image className="h-3 w-3" />
              Image
            </span>
          ) : ad.isVideo ? (
            <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              <Video className="h-3 w-3" />
              Video
            </span>
          ) : (
            <span className="text-xs bg-surface-subtle text-muted px-1.5 py-0.5 rounded">
              Unsupported
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className={cn(
            'text-xs',
            ad.status === 'ACTIVE' ? 'text-success' : 'text-muted'
          )}>
            {ad.status}
          </span>
          {ad.isVideo && ad.videoDuration && (
            <span className="flex items-center gap-1 text-xs text-muted">
              <Clock className="h-3 w-3" />
              {formatDuration(ad.videoDuration)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
